import { TextButton } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { DatePickerInput } from '@creditornot/cb-date-picker';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { Fragment, useCallback, useEffect } from 'react';
import { Field, useField, useForm, useFormState } from 'react-final-form';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { TaxInfoVerificationResult } from 'apis/financial-data-registry/types';
import { KybSubmissionData, LegalForm, TinDetails, isLegalFormValue } from 'apis/legitimizer/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import { useMerchantContext } from 'modules/merchant/MerchantContext';

import { TaxInfoResultAlert } from './TaxInfoResultAlert';
import { taxIdKeyToLabelMap } from './useTaxInfoVerification';
import {
  ALL_COUNTRIES,
  COUNTRY_CODES,
  COUNTRY_CODE_EXTENSIONS,
  ALL_NATIONALITIES,
  SECONDARY_LIABILITY_COUNTRIES,
} from '../countryCodes';
import { fieldOfIndustryOptions, legalFormOptions } from '../mappings';
import { formatDate } from '../transformers';
import {
  BasicInfoFormFieldsConfig,
  BasicInfoFormValues,
  BusinessRepresentative,
  BusinessRepresentativeFieldsConfig,
  PhoneNumber,
} from '../types';
import { YEAR_OF_BIRTH_RANGE, YEAR_OF_REGISTRATION_RANGE } from '../yearRanges';

type Props = {
  submitting: boolean;
  taxInfoVerificationResult: TaxInfoVerificationResult;
  basicInfoFieldsConfig: BasicInfoFormFieldsConfig;
  businessRepresentativeFieldsConfig: BusinessRepresentativeFieldsConfig;
  submissionData: KybSubmissionData;
  onTaxInfoValuesDirtyStatusChange: (areFieldsDirty: boolean) => void;
};

const FormRowContent = styled.div`
  display: flex;
  flex-direction: column;

  & > * + * {
    margin-block-start: 8px;
  }
`;

const FormRowInfo = styled.div``;

const FieldSet = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const Legend = styled.legend`
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
`;

const DateTimeContainer = styled.div`
  > div {
    display: block;
  }
`;

const InputWrapper = styled.div`
  margin-block-start: 10px;
  display: flex;
`;

const RegistrationDateWrapper = styled.div`
  display: flex;
`;

const InputNarrow = styled.div`
  flex: 0 0 165px;
  margin-inline-end: 8px;
`;

const InputExtend = styled.div`
  flex: 1 1 auto;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  margin-block-end: 16px;
`;

const AddButton = styled(TextButton).attrs({ variant: 'brand' })`
  margin-block-end: 16px;
`;

const RemoveButton = styled(TextButton).attrs({ variant: 'negative' })`
  padding: 2px;
`;

const RemoveButtonWrapper = styled.div`
  float: inline-end;
  text-align: end;
`;

const StyledInputContainer = styled(InputContainer)`
  margin-block-start: 10px;
`;

const BusinessRepresentativeContainer = styled.div``;

const InfoLink = styled.a``;

const TaxInfoFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const allCountries: { label: string; value: string }[] = ALL_COUNTRIES.map(country => ({
  label: country.value,
  value: country.id,
}));

const allNationalities: { label: string; value: string }[] = ALL_NATIONALITIES.map(country => ({
  label: country.value,
  value: country.id,
}));

const companyRepresentativesNameFieldLabelKeyByCountry = {
  GRC: 'compliance.kyb.sections.basic-info.company-representatives.full-name-country.grc',
  HUN: 'compliance.kyb.sections.basic-info.company-representatives.full-name-country.hun',
};

const isCustomRepresentativeNameLabelCountry = (
  country: Country,
): country is keyof typeof companyRepresentativesNameFieldLabelKeyByCountry => {
  return Object.keys(companyRepresentativesNameFieldLabelKeyByCountry).includes(country);
};

const getCompanyRepresentativeNameFieldLabel = (country: Country) => {
  if (isCustomRepresentativeNameLabelCountry(country)) {
    return companyRepresentativesNameFieldLabelKeyByCountry[country];
  }

  return 'compliance.kyb.sections.basic-info.company-representatives.full-name';
};

const BasicInfoFormFields = ({
  submitting,
  taxInfoVerificationResult,
  basicInfoFieldsConfig,
  businessRepresentativeFieldsConfig,
  submissionData,
  onTaxInfoValuesDirtyStatusChange,
}: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const { merchant } = useMerchantContext();

  const companyRepresentativeNameFieldLabel = getCompanyRepresentativeNameFieldLabel(merchant.country);

  const { dirtyFieldsSinceLastSubmit } = useFormState();

  useEffect(() => {
    const areTaxInfoFieldsDirty = Object.keys(dirtyFieldsSinceLastSubmit).some(value =>
      ['isVatLiable', 'vatId', 'tin', 'companyName', 'streetAddress', 'postalCode', 'city'].includes(value),
    );
    onTaxInfoValuesDirtyStatusChange(areTaxInfoFieldsDirty);
  }, [dirtyFieldsSinceLastSubmit, onTaxInfoValuesDirtyStatusChange]);

  const isVatLiable = useField<boolean | undefined>('isVatLiable').input.value;
  const additionalTinsFieldsArray = useFieldArray<TinDetails>('additionalTins');

  const secondaryLiabilityCountriesOptions = SECONDARY_LIABILITY_COUNTRIES.map(country => ({
    label: country.value,
    value: country.id,
  }));

  const { change } = useForm();
  const legalForm = useField<LegalForm>('legalForm').input.value;
  const representatives = useFieldArray<BusinessRepresentative>('businessRepresentatives').fields.value;
  const ubos = submissionData.beneficiaries?.uboDetails || [];

  const handleLegalFormChange = (inputOnChange: (value: string | null) => void) => (value: string | null) => {
    const isSoleTrader = isLegalFormValue(value) && value === 'SOLE_TRADER';
    const representativeMatchesUbo =
      representatives.length === 1 && ubos.length === 1 && representatives[0].entityUUID === ubos[0].entityUUID;
    const noUbosSubmitted = ubos.length === 0 || (ubos.length === 1 && !ubos[0].entityUUID);

    if (isSoleTrader && (representativeMatchesUbo || noUbosSubmitted)) {
      change('isRepresentativeSoleBusinessOwner', true);
    }

    inputOnChange(value);
  };

  const showRepresentativeAddressFields = legalForm === 'SOLE_TRADER' && representatives.length === 1;

  return (
    <FieldSet>
      <Legend>
        <T.Heading6>
          <FormattedMessage id="compliance.kyb.sections.basic-info.business-information.title" />
        </T.Heading6>
      </Legend>
      {basicInfoFieldsConfig.companyName.visible && (
        <FormRow
          required={basicInfoFieldsConfig.companyName.required}
          label={t('compliance.kyb.sections.basic-info.business-information.name')}
          labelHtmlFor="companyName"
          info={<FormRowInfo>{t('compliance.kyb.sections.basic-info.business-information.name-info')}</FormRowInfo>}
        >
          <Field<BasicInfoFormValues['companyName']> name="companyName">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="companyName" disabled={submitting} />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      <FormRow
        required={basicInfoFieldsConfig.streetAddress.required || basicInfoFieldsConfig.postalCode.required}
        label={t('compliance.kyb.sections.basic-info.business-information.address')}
      >
        <FormRowContent>
          {basicInfoFieldsConfig.streetAddress.visible && (
            <Field<BasicInfoFormValues['streetAddress']> name="streetAddress">
              {field => (
                <InputContainer label={t('compliance.kyb.address.street-address')} labelHtmlFor="streetAddress">
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="streetAddress" disabled={submitting} />}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>
          )}
          <InputWrapper>
            {basicInfoFieldsConfig.postalCode.visible && (
              <InputNarrow>
                <Field<BasicInfoFormValues['postalCode']> name="postalCode">
                  {field => (
                    <InputContainer label={t('compliance.kyb.address.postal-code')} labelHtmlFor="postalCode">
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} id="postalCode" disabled={submitting} />}
                      </RFFFieldErrorWrapper>
                    </InputContainer>
                  )}
                </Field>
              </InputNarrow>
            )}
            {basicInfoFieldsConfig.city.visible && (
              <InputExtend>
                <Field<BasicInfoFormValues['city']> name="city">
                  {field => (
                    <InputContainer label={t('compliance.kyb.address.city')} labelHtmlFor="city">
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} id="city" disabled={submitting} />}
                      </RFFFieldErrorWrapper>
                    </InputContainer>
                  )}
                </Field>
              </InputExtend>
            )}
          </InputWrapper>
          {basicInfoFieldsConfig.countryCode.visible && (
            <Field<BasicInfoFormValues['countryCode']> name="countryCode">
              {field => (
                <InputContainer label={t('compliance.kyb.address.country')} labelHtmlFor="countryCode">
                  <RFFFieldErrorWrapper field={field}>
                    {input => (
                      <Select
                        inputId="countryCode"
                        disabled
                        options={COUNTRY_CODES.map(country => ({
                          value: country.id,
                          label: country.value,
                        }))}
                        {...input}
                      />
                    )}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>
          )}
        </FormRowContent>
      </FormRow>
      {basicInfoFieldsConfig.registrationNumber.visible && (
        <FormRow
          required={basicInfoFieldsConfig.registrationNumber.required}
          label={t('compliance.kyb.sections.basic-info.business-information.business-id')}
          labelHtmlFor="registrationNumber"
        >
          <Field<BasicInfoFormValues['registrationNumber']> name="registrationNumber">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="registrationNumber" disabled={submitting} />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {basicInfoFieldsConfig.isVatLiable.visible && (
        <FormRow
          required={basicInfoFieldsConfig.isVatLiable.required}
          label={t('compliance.kyb.sections.basic-info.business-information.is-vat-liable')}
          labelHtmlFor="isVatLiable"
        >
          <TaxInfoFieldWrapper>
            <Field<BasicInfoFormValues['isVatLiable']> name="isVatLiable" type="checkbox">
              {field => (
                <InputContainer>
                  <RFFFieldErrorWrapper field={field}>
                    {({ value, ...input }) => <InputCheckbox {...input} id="isVatLiable" disabled={submitting} />}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>
          </TaxInfoFieldWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.vatId.visible && (
        <FormRow
          required={basicInfoFieldsConfig.vatId.required || (!!isVatLiable && merchant.country !== 'DEU')}
          label={t('compliance.kyb.sections.basic-info.business-information.vat-id')}
          labelHtmlFor="vatId"
        >
          <TaxInfoFieldWrapper>
            <Field<BasicInfoFormValues['vatId']> name="vatId">
              {field => (
                <InputContainer>
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="vatId" disabled={submitting} />}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>

            {taxInfoVerificationResult.vatId && <TaxInfoResultAlert taxIdLabel={taxIdKeyToLabelMap.vatId} />}
          </TaxInfoFieldWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.tin.visible && (
        <FormRow
          required={basicInfoFieldsConfig.tin.required}
          label={t('compliance.kyb.sections.basic-info.business-information.tin')}
          labelHtmlFor="tin"
        >
          <TaxInfoFieldWrapper>
            <Field<BasicInfoFormValues['tin']> name="tin">
              {field => (
                <InputContainer>
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="tin" disabled={submitting} />}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>

            {taxInfoVerificationResult.tin && <TaxInfoResultAlert taxIdLabel={taxIdKeyToLabelMap.tin} />}
          </TaxInfoFieldWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.isSecondaryTinLiable.visible && (
        <FormRow
          required
          label={t('compliance.kyb.sections.basic-info.business-information.secondary-tin-liable')}
          labelHtmlFor="isSecondaryTinLiable"
        >
          <TaxInfoFieldWrapper>
            <Field<BasicInfoFormValues['isSecondaryTinLiable']> name="isSecondaryTinLiable" type="checkbox">
              {field => (
                <InputContainer>
                  <RFFFieldErrorWrapper field={field}>
                    {({ value, onChange, ...input }) => (
                      <InputCheckbox
                        {...input}
                        id="isSecondaryTinLiable"
                        disabled={submitting}
                        onChange={e => {
                          if (e.target.checked) {
                            if (additionalTinsFieldsArray.fields.length === 0) {
                              additionalTinsFieldsArray.fields.push({
                                countryCode: undefined,
                                number: undefined,
                              });
                            }
                          } else {
                            // There is a bug with react-final-form where rendering fields conditionally causes validations to run
                            // and it triggers a setState which causes some react warnings. To solve this instead of rendering
                            // the field conditionally based on the isSecondaryTinLiable value, we always render it but mutate
                            // the additionalTinsFieldsArray.
                            // More information here: https://github.com/final-form/react-final-form/issues/809#issuecomment-670454285
                            const additionalTinsFieldsArrayLength = additionalTinsFieldsArray.fields.length || 0;
                            for (let i = additionalTinsFieldsArrayLength; i > 0; i--) {
                              additionalTinsFieldsArray.fields.pop();
                            }
                          }

                          onChange(e);
                        }}
                      />
                    )}
                  </RFFFieldErrorWrapper>
                </InputContainer>
              )}
            </Field>
          </TaxInfoFieldWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.additionalTins.visible && (
        <FieldArray<TinDetails> name="additionalTins">
          {({ fields }) => (
            <>
              {fields.map((name: string) => (
                <Fragment key={name}>
                  <Field<string | undefined> name={`${name}.countryCode`}>
                    {field => (
                      <FormRow
                        label={t('compliance.kyb.sections.basic-info.business-information.secondary-tin-country')}
                        labelHtmlFor={`${name}.countryCode`}
                        required
                      >
                        <RFFFieldErrorWrapper field={field}>
                          {input => (
                            <Select
                              {...input}
                              disabled={submitting}
                              inputId={`${name}.countryCode`}
                              options={secondaryLiabilityCountriesOptions}
                            />
                          )}
                        </RFFFieldErrorWrapper>
                      </FormRow>
                    )}
                  </Field>

                  <Field<string | undefined> name={`${name}.number`}>
                    {field => (
                      <FormRow
                        label={t('compliance.kyb.sections.basic-info.business-information.secondary-tin-number')}
                        labelHtmlFor={`${name}.number`}
                        required
                      >
                        <RFFFieldErrorWrapper field={field}>
                          {input => <Input {...input} id={`${name}.number`} disabled={submitting} />}
                        </RFFFieldErrorWrapper>
                      </FormRow>
                    )}
                  </Field>
                </Fragment>
              ))}
            </>
          )}
        </FieldArray>
      )}
      {basicInfoFieldsConfig.lucidId.visible && (
        <FormRow
          required={basicInfoFieldsConfig.lucidId.required}
          label={t('compliance.kyb.sections.basic-info.business-information.lucid-id.name')}
          labelHtmlFor="lucidId"
          info={
            <FormRowInfo>
              {`${t('compliance.kyb.sections.basic-info.business-information.lucid-id.hint.question')} `}
              <InfoLink
                target="_blank"
                href="https://www.verpackungsregister.org/information-orientierung/hilfe-erklaerung/erklaerfilme/ziele-aufgaben-der-zsvr"
              >
                {t('compliance.kyb.sections.basic-info.business-information.lucid-id.hint.learn-more')}
              </InfoLink>
            </FormRowInfo>
          }
        >
          <Field<BasicInfoFormValues['lucidId']> name="lucidId">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} id="lucidId" disabled={submitting} />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {basicInfoFieldsConfig.legalForm.visible && (
        <FormRow
          required={basicInfoFieldsConfig.legalForm.required}
          label={t('compliance.kyb.sections.basic-info.business-information.legal-form')}
          labelHtmlFor="legalForm"
        >
          <Field<BasicInfoFormValues['legalForm']> name="legalForm" key="legalForm">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <Select
                    {...input}
                    inputId="legalForm"
                    options={legalFormOptions.map(({ translationKey, value }) => ({
                      label: t(translationKey),
                      value,
                    }))}
                    disabled={submitting}
                    onChange={handleLegalFormChange(input.onChange)}
                  />
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
      )}
      {basicInfoFieldsConfig.dateOfRegistration.visible && (
        <FormRow
          required={basicInfoFieldsConfig.dateOfRegistration.required}
          label={t('compliance.kyb.sections.basic-info.business-information.registration-date')}
          labelHtmlFor="dateOfRegistration"
          info={
            <FormRowInfo>
              {t('compliance.kyb.sections.basic-info.business-information.registration-date-info')}
            </FormRowInfo>
          }
        >
          <RegistrationDateWrapper>
            <InputNarrow>
              <Field<BasicInfoFormValues['dateOfRegistration']> name="dateOfRegistration">
                {field => (
                  <InputContainer label={t('compliance.kyb.date-time.date')} labelHtmlFor="dateOfRegistration">
                    <DateTimeContainer>
                      <RFFFieldErrorWrapper field={field}>
                        {input => (
                          <DatePickerInput
                            {...input}
                            closeOnChange
                            formatValue={formatDate}
                            disableYearSelect
                            popoverZIndex={100005}
                            disabled={submitting}
                          />
                        )}
                      </RFFFieldErrorWrapper>
                    </DateTimeContainer>
                  </InputContainer>
                )}
              </Field>
            </InputNarrow>
            <InputExtend>
              <Field<BasicInfoFormValues['yearOfRegistration']> name="yearOfRegistration">
                {field => (
                  <InputContainer label={t('compliance.kyb.date-time.year')} labelHtmlFor="yearOfRegistration">
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Select
                          {...input}
                          options={YEAR_OF_REGISTRATION_RANGE}
                          inputId="yearOfRegistration"
                          disabled={submitting}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  </InputContainer>
                )}
              </Field>
            </InputExtend>
          </RegistrationDateWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.taxCountryCode.visible && (
        <FormRow
          required={basicInfoFieldsConfig.taxCountryCode.required}
          label={t('compliance.kyb.sections.basic-info.business-information.country-of-taxation')}
          labelHtmlFor="taxCountryCode"
        >
          <Field<BasicInfoFormValues['taxCountryCode']> name="taxCountryCode" key="taxCountryCode">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => <Select inputId="taxCountryCode" options={allCountries} {...input} disabled={submitting} />}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
      )}
      {basicInfoFieldsConfig.fieldOfIndustry.visible && (
        <FormRow
          required={basicInfoFieldsConfig.fieldOfIndustry.required}
          label={t('compliance.kyb.sections.basic-info.business-information.field-of-industry')}
          labelHtmlFor="fieldOfIndustry"
        >
          <Field<BasicInfoFormValues['fieldOfIndustry']> name="fieldOfIndustry" key="fieldOfIndustry">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <Select
                    inputId="fieldOfIndustry"
                    options={fieldOfIndustryOptions.map(({ translationKey, value }) => ({
                      label: t(translationKey),
                      value,
                    }))}
                    disabled={submitting}
                    {...input}
                  />
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
      )}
      {basicInfoFieldsConfig.phone.visible && (
        <FormRow
          required={basicInfoFieldsConfig.phone.required}
          label={t('compliance.kyb.sections.basic-info.business-information.phone-number')}
        >
          <InputWrapper>
            <InputNarrow>
              <Field<PhoneNumber['extension']> name="phone.extension">
                {field => (
                  <InputContainer>
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <Select
                          inputId="phone.extension"
                          options={COUNTRY_CODE_EXTENSIONS.map(country => ({
                            value: country.id,
                            label: country.value,
                          }))}
                          disabled={submitting}
                          {...input}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  </InputContainer>
                )}
              </Field>
            </InputNarrow>
            <InputExtend>
              <Field<PhoneNumber['number']> name="phone.number">
                {field => (
                  <InputContainer>
                    <RFFFieldErrorWrapper field={field}>
                      {input => <Input {...input} disabled={submitting} />}
                    </RFFFieldErrorWrapper>
                  </InputContainer>
                )}
              </Field>
            </InputExtend>
          </InputWrapper>
        </FormRow>
      )}
      {basicInfoFieldsConfig.businessRepresentatives.visible && (
        <FormRow
          required={basicInfoFieldsConfig.businessRepresentatives.required}
          label={t('compliance.kyb.sections.basic-info.company-representatives.title')}
        >
          <FormRowContent>
            <FieldArray<BusinessRepresentative> name="businessRepresentatives">
              {({ fields }) => (
                <>
                  {fields.map((name: string, index: number) => {
                    const { isMainRepresentative } = fields.value[index];
                    return (
                      <BusinessRepresentativeContainer key={name} data-test-id={name}>
                        {businessRepresentativeFieldsConfig.name.visible && (
                          <Field<BusinessRepresentative['name']> name={`${name}.name`} key={`${name}.name`}>
                            {field => (
                              <InputContainer
                                label={t(companyRepresentativeNameFieldLabel)}
                                labelHtmlFor={`${name}.name`}
                              >
                                <RFFFieldErrorWrapper field={field} key={`${name}.name`}>
                                  {input => (
                                    <Input
                                      {...input}
                                      id={`${name}.name`}
                                      disabled={submitting || isMainRepresentative}
                                    />
                                  )}
                                </RFFFieldErrorWrapper>
                              </InputContainer>
                            )}
                          </Field>
                        )}
                        {businessRepresentativeFieldsConfig.dateOfBirth.visible &&
                          businessRepresentativeFieldsConfig.yearOfBirth.visible && (
                            <InputWrapper>
                              <InputNarrow>
                                <Field<BusinessRepresentative['dateOfBirth']>
                                  name={`${name}.dateOfBirth`}
                                  key={`${name}.dateOfBirth`}
                                >
                                  {field => (
                                    <InputContainer
                                      label={t('compliance.kyb.person-info.date-of-birth')}
                                      labelHtmlFor={`${name}.dateOfBirth`}
                                    >
                                      <DateTimeContainer>
                                        <RFFFieldErrorWrapper field={field}>
                                          {input => (
                                            <DatePickerInput
                                              {...input}
                                              closeOnChange
                                              formatValue={formatDate}
                                              disableYearSelect
                                              popoverZIndex={100005}
                                              disabled={submitting}
                                            />
                                          )}
                                        </RFFFieldErrorWrapper>
                                      </DateTimeContainer>
                                    </InputContainer>
                                  )}
                                </Field>
                              </InputNarrow>
                              <InputExtend>
                                <Field<BusinessRepresentative['yearOfBirth']>
                                  name={`${name}.yearOfBirth`}
                                  key={`${name}.yearOfBirth`}
                                >
                                  {field => (
                                    <InputContainer
                                      label={t('compliance.kyb.person-info.year-of-birth')}
                                      labelHtmlFor={`${name}.yearOfBirth`}
                                    >
                                      <RFFFieldErrorWrapper field={field}>
                                        {input => (
                                          <Select
                                            {...input}
                                            options={YEAR_OF_BIRTH_RANGE}
                                            inputId={`${name}.yearOfBirth`}
                                            disabled={submitting}
                                          />
                                        )}
                                      </RFFFieldErrorWrapper>
                                    </InputContainer>
                                  )}
                                </Field>
                              </InputExtend>
                            </InputWrapper>
                          )}
                        {businessRepresentativeFieldsConfig.placeOfBirth.visible && (
                          <Field<BusinessRepresentative['placeOfBirth']> name={`${name}.placeOfBirth`}>
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.person-info.place-of-birth')}
                                labelHtmlFor={`${name}.placeOfBirth`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => <Input {...input} id={`${name}.placeOfBirth`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}

                        {businessRepresentativeFieldsConfig.street.visible && showRepresentativeAddressFields && (
                          <Field<BusinessRepresentative['street']> name={`${name}.street`} key={`${name}.street`}>
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.address.street-address')}
                                labelHtmlFor={`${name}.street`}
                              >
                                <RFFFieldErrorWrapper field={field} key={`${name}.street`}>
                                  {input => <Input {...input} id={`${name}.street`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}

                        {showRepresentativeAddressFields && (
                          <InputWrapper>
                            {businessRepresentativeFieldsConfig.postalCode.visible && (
                              <InputNarrow>
                                <Field<BusinessRepresentative['postalCode']>
                                  name={`${name}.postalCode`}
                                  key={`${name}.postalCode`}
                                >
                                  {field => (
                                    <InputContainer
                                      label={t('compliance.kyb.address.postal-code')}
                                      labelHtmlFor={`${name}.postalCode`}
                                    >
                                      <RFFFieldErrorWrapper field={field}>
                                        {input => <Input {...input} id={`${name}.postalCode`} disabled={submitting} />}
                                      </RFFFieldErrorWrapper>
                                    </InputContainer>
                                  )}
                                </Field>
                              </InputNarrow>
                            )}
                            {businessRepresentativeFieldsConfig.city.visible && (
                              <InputExtend>
                                <Field<BusinessRepresentative['city']> name={`${name}.city`} key={`${name}.city`}>
                                  {field => (
                                    <InputContainer
                                      label={t('compliance.kyb.address.city')}
                                      labelHtmlFor={`${name}.city`}
                                    >
                                      <RFFFieldErrorWrapper field={field}>
                                        {input => <Input {...input} id={`${name}.city`} disabled={submitting} />}
                                      </RFFFieldErrorWrapper>
                                    </InputContainer>
                                  )}
                                </Field>
                              </InputExtend>
                            )}
                          </InputWrapper>
                        )}
                        {businessRepresentativeFieldsConfig.countryOfResidence.visible && (
                          <Field<BusinessRepresentative['countryOfResidence']>
                            name={`${name}.countryOfResidence`}
                            key={`${name}.countryOfResidence`}
                          >
                            {field => (
                              <StyledInputContainer
                                label={t(
                                  'compliance.kyb.sections.basic-info.business-information.country-of-residence',
                                )}
                                labelHtmlFor={`${name}.countryOfResidence`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => (
                                    <Select
                                      inputId="countryOfResidence"
                                      options={allCountries}
                                      disabled={submitting}
                                      {...input}
                                    />
                                  )}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        {businessRepresentativeFieldsConfig.nationality.visible && (
                          <Field<BusinessRepresentative['nationality']>
                            name={`${name}.nationality`}
                            key={`${name}.nationality`}
                          >
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.person-info.nationality')}
                                labelHtmlFor={`${name}.nationality`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => (
                                    <Select
                                      inputId={`${name}.nationality`}
                                      options={allNationalities}
                                      disabled={submitting}
                                      {...input}
                                    />
                                  )}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        {businessRepresentativeFieldsConfig.nationalId.visible && (
                          <Field<BusinessRepresentative['nationalId']> name={`${name}.nationalId`}>
                            {field => (
                              <StyledInputContainer
                                label={t('compliance.kyb.sections.basic-info.business-information.national-id')}
                                labelHtmlFor={`${name}.nationalId`}
                              >
                                <RFFFieldErrorWrapper field={field}>
                                  {input => <Input {...input} id={`${name}.nationalId`} disabled={submitting} />}
                                </RFFFieldErrorWrapper>
                              </StyledInputContainer>
                            )}
                          </Field>
                        )}
                        <FormRow label={''}>
                          {index !== 0 && (
                            <RemoveButtonWrapper>
                              <RemoveButton icon={<Trashbin />} onClick={() => fields.remove(index)}>
                                <FormattedMessage id="compliance.kyb.person-info.remove" />
                              </RemoveButton>
                            </RemoveButtonWrapper>
                          )}
                        </FormRow>
                        <Divider />
                      </BusinessRepresentativeContainer>
                    );
                  })}
                  <AddButton onClick={() => fields.push({})}>
                    <FormattedMessage id="compliance.kyb.sections.basic-info.company-representatives.add" />
                  </AddButton>
                </>
              )}
            </FieldArray>
          </FormRowContent>
        </FormRow>
      )}
    </FieldSet>
  );
};

export default BasicInfoFormFields;
